import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/App';
import MainLayout from '@/components/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  FileText, 
  Download, 
  Filter,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Clock,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const AdminInvoicesPage = () => {
  const navigate = useNavigate();
  const { getAuthHeaders, user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    therapist_id: '',
    payment_status: '',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    fetchTherapists();
    fetchInvoices();
  }, []);

  const fetchTherapists = async () => {
    try {
      const response = await fetch(`${API}/admin/therapists`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (response.status === 403) {
        setAccessDenied(true);
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        setTherapists(data);
      }
    } catch (error) {
    }
  };

  const fetchInvoices = async () => {
    try {
      // Build query params
      const params = new URLSearchParams();
      if (filters.therapist_id) params.append('therapist_id', filters.therapist_id);
      if (filters.payment_status) params.append('payment_status', filters.payment_status);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      
      const url = `${API}/admin/invoices${params.toString() ? '?' + params.toString() : ''}`;
      
      const response = await fetch(url, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (response.status === 403) {
        setAccessDenied(true);
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        setInvoices(data);
      }
    } catch (error) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchInvoices();
    setRefreshing(false);
    toast.success('Invoices refreshed');
  };

  const handleApplyFilters = () => {
    setLoading(true);
    fetchInvoices();
  };

  const handleClearFilters = () => {
    setFilters({
      therapist_id: '',
      payment_status: '',
      start_date: '',
      end_date: ''
    });
    setLoading(true);
    setTimeout(() => fetchInvoices(), 100);
  };

  const handleDownloadPdf = async (invoiceId, invoiceNumber) => {
    try {
      const response = await fetch(`${API}/admin/invoices/${invoiceId}/pdf`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to download PDF');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const getPaymentStatusBadge = (status) => {
    if (status === 'paid') {
      return (
        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Paid
        </Badge>
      );
    }
    return (
      <Badge className="bg-amber-100 text-amber-700 border-amber-200">
        <Clock className="h-3 w-3 mr-1" />
        Pending
      </Badge>
    );
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Access denied view
  if (accessDenied) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh]" data-testid="access-denied">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <XCircle className="h-8 w-8 text-red-500" />
            </div>
            <h1 
              className="text-2xl font-semibold text-foreground mb-2"
              style={{ fontFamily: 'Fraunces, serif' }}
            >
              Access Denied
            </h1>
            <p className="text-muted-foreground mb-6">
              You don't have permission to access this page.<br />
              Admin access is required.
            </p>
            <Button onClick={() => navigate('/dashboard')} className="rounded-xl">
              Return to Dashboard
            </Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="admin-invoices-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 
              className="text-2xl sm:text-3xl font-semibold text-foreground"
              style={{ fontFamily: 'Fraunces, serif' }}
            >
              Admin Invoice History
            </h1>
            <p className="text-muted-foreground mt-1">
              View all therapist invoices across the platform
            </p>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRefresh}
            disabled={refreshing}
            className="rounded-xl"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Filters Card */}
        <Card className="border-border">
          <CardHeader className="py-4">
            <CardTitle className="text-base flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* Therapist Filter */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Therapist</Label>
                <Select 
                  value={filters.therapist_id || "all"} 
                  onValueChange={(value) => setFilters(prev => ({ ...prev, therapist_id: value === "all" ? "" : value }))}
                >
                  <SelectTrigger className="rounded-xl" data-testid="therapist-filter">
                    <SelectValue placeholder="All Therapists" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Therapists</SelectItem>
                    {therapists.map((t) => (
                      <SelectItem key={t.user_id} value={t.user_id}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Payment Status Filter */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Payment Status</Label>
                <Select 
                  value={filters.payment_status || "all"} 
                  onValueChange={(value) => setFilters(prev => ({ ...prev, payment_status: value === "all" ? "" : value }))}
                >
                  <SelectTrigger className="rounded-xl" data-testid="status-filter">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Date Range */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Start Date</Label>
                <Input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => setFilters(prev => ({ ...prev, start_date: e.target.value }))}
                  className="rounded-xl"
                  data-testid="start-date-filter"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">End Date</Label>
                <Input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => setFilters(prev => ({ ...prev, end_date: e.target.value }))}
                  className="rounded-xl"
                  data-testid="end-date-filter"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex items-end gap-2">
                <Button 
                  onClick={handleApplyFilters} 
                  className="rounded-xl flex-1"
                  data-testid="apply-filters-btn"
                >
                  Apply
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleClearFilters}
                  className="rounded-xl"
                  data-testid="clear-filters-btn"
                >
                  Clear
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Invoices Table */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Fraunces, serif' }}>
              <FileText className="h-5 w-5 inline mr-2 text-primary" />
              Invoices ({invoices.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12 text-muted-foreground">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4" />
                Loading invoices...
              </div>
            ) : invoices.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-4 opacity-40" />
                <p className="font-medium">No invoices found</p>
                <p className="text-sm mt-1">
                  {filters.therapist_id || filters.payment_status || filters.start_date || filters.end_date
                    ? 'Try adjusting your filters'
                    : 'No invoices have been created yet'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Therapist</TableHead>
                      <TableHead>Patient</TableHead>
                      <TableHead>Invoice ID</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map((invoice) => (
                      <TableRow key={invoice.invoice_id} data-testid={`invoice-row-${invoice.invoice_id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-foreground">{invoice.therapist_name}</p>
                            <p className="text-xs text-muted-foreground">{invoice.therapist_email}</p>
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">
                          {invoice.patient_name}
                        </TableCell>
                        <TableCell>
                          <span className="font-mono text-sm text-muted-foreground">
                            {invoice.invoice_number}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatAmount(invoice.total_amount)}
                        </TableCell>
                        <TableCell>
                          {getPaymentStatusBadge(invoice.payment_status)}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {invoice.created_at && format(new Date(invoice.created_at), 'MMM d, yyyy')}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadPdf(invoice.invoice_id, invoice.invoice_number)}
                            className="h-8 w-8 p-0"
                            title="Download PDF"
                            data-testid={`download-pdf-${invoice.invoice_id}`}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default AdminInvoicesPage;
